import { useEffect, useState } from 'react';
import {
  Alert,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Button,
  TextField,
  Box,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Typography,
  useTheme,
  alpha,
  Tooltip,
  Snackbar,
  Chip,
  Theme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import { CreateEstimate } from './CreateEstimate';
import { useUnityBuildStore } from '../../stores/store';
import { useFetchEstimates } from '../../hooks/useFetchProjectEstimates';
import { useDeleteEstimateMutation } from '../../hooks/useCreateProjectEstimateMutation';
import { useFetchContracts } from '../../hooks/useFetchContracts';
import { useStore } from 'zustand';
import {
  Estimate,
  GetProjectEstimateResponse,
} from '../../api/protosCompiled/projectEstimate/projectEstimate_pb';
import { useHandleSetEstimate } from './helpers';
import { formatDateForDisplay } from '../../utils/dateUtils';

// Import responsive utilities
import { useResponsive, useOrientation, ResponsiveHelpers } from '../../theme';

// Define the action type to match the helper's expected type
type HandleSetEstimateField = 'create' | 'update' | 'setEstimate';

// Helper function to get status chip styling based on status name and theme
export const getStatusChipProps = (statusName: string, theme: Theme) => {
  const isDark = theme.palette.mode === 'dark';

  const statusLower = statusName.toLowerCase();

  // Handle specific status types
  if (statusLower === 'pending') {
    return {
      color: isDark ? '#fff3cd' : '#856404',
      backgroundColor: isDark ? '#664d03' : '#fff3cd',
      borderColor: isDark ? '#ffecb5' : '#ffeaa7',
    };
  }

  if (statusLower === 'accepted') {
    return {
      color: isDark ? '#d4edda' : '#155724',
      backgroundColor: isDark ? '#1b5e20' : '#d4edda',
      borderColor: isDark ? '#81c784' : '#c3e6cb',
    };
  }

  if (statusLower === 'rejected') {
    return {
      color: isDark ? '#f8d7da' : '#721c24',
      backgroundColor: isDark ? '#c62828' : '#f8d7da',
      borderColor: isDark ? '#e57373' : '#f5c6cb',
    };
  }

  if (statusLower === 'void') {
    return {
      color: isDark ? '#e2e3e5' : '#383d41',
      backgroundColor: isDark ? '#495057' : '#e2e3e5',
      borderColor: isDark ? '#adb5bd' : '#d1d3d4',
    };
  }

  // Fallback for any other status types
  if (
    statusLower.includes('progress') ||
    statusLower.includes('active') ||
    statusLower.includes('working')
  ) {
    return {
      color: isDark ? '#cce7ff' : '#004085',
      backgroundColor: isDark ? '#0d47a1' : '#cce7ff',
      borderColor: isDark ? '#90caf9' : '#81c784',
    };
  }

  if (
    statusLower.includes('completed') ||
    statusLower.includes('finished') ||
    statusLower.includes('done')
  ) {
    return {
      color: isDark ? '#d4edda' : '#155724',
      backgroundColor: isDark ? '#1b5e20' : '#d4edda',
      borderColor: isDark ? '#81c784' : '#c3e6cb',
    };
  }

  if (
    statusLower.includes('hold') ||
    statusLower.includes('paused') ||
    statusLower.includes('suspended')
  ) {
    return {
      color: isDark ? '#e2e3e5' : '#383d41',
      backgroundColor: isDark ? '#495057' : '#e2e3e5',
      borderColor: isDark ? '#adb5bd' : '#d1d3d4',
    };
  }

  if (statusLower.includes('draft') || statusLower.includes('new')) {
    return {
      color: isDark ? '#f8f9fa' : '#495057',
      backgroundColor: isDark ? '#6c757d' : '#f8f9fa',
      borderColor: isDark ? '#adb5bd' : '#dee2e6',
    };
  }

  // Default styling for unknown statuses
  return {
    color: isDark ? '#e9ecef' : '#495057',
    backgroundColor: isDark ? '#6c757d' : '#e9ecef',
    borderColor: isDark ? '#adb5bd' : '#ced4da',
  };
};

// Helper function to get descriptive status text based on status name
export const getStatusDescription = (statusName: string): string => {
  const statusLower = statusName.toLowerCase();

  switch (statusLower) {
    case 'draft':
      return "Your estimate is still being developed. Use this while you're building quantities, pricing, and finalizing your bid strategy.";
    case 'pending':
      return 'Your estimate is complete and waiting for internal review, pricing approval, or submission decision. Great for tracking bids in your approval process.';
    case 'rejected':
      return "The client has declined your bid. Use this to track projects where you submitted but weren't selected, helping you analyze win rates.";
    case 'declined':
      return "Your team decided not to pursue or submit this bid. Maybe the project wasn't a good fit, timing was wrong, or you chose not to compete.";
    case 'void':
      return 'The estimate is no longer valid - perhaps the project was cancelled, scope changed dramatically, or you need to start over with a new estimate.';
    case 'accepted':
      return 'Congratulations! You won the bid. This status unlocks contract creation and locks your estimate to preserve the winning numbers.';
    default:
      return statusName;
  }
};

export function ListEstimates() {
  const theme = useTheme();
  const responsive = useResponsive() as ResponsiveHelpers;
  const { isMobile, isTablet } = responsive;
  const { isLandscape } = useOrientation();

  const selectedProject = useStore(useUnityBuildStore).selectedProject;
  const isViewingEstimateForm = useStore(useUnityBuildStore).isViewingEstimateForm;
  const setIsViewingEstimateForm = useStore(useUnityBuildStore).setIsViewingEstimateForm;
  const isCreatingEstimate = useStore(useUnityBuildStore).isCreatingEstimate;

  const { data: estimateList, error, isLoading } = useFetchEstimates();
  const { data: contractList } = useFetchContracts();

  // Add state for success notification
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Add state for error notification
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [deleteErrorMessage, setDeleteErrorMessage] = useState('');

  // Add state for delete confirmation dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [estimateToDelete, setEstimateToDelete] = useState<Estimate.AsObject | null>(null);

  // Delete mutation hook
  const deleteEstimateMutation = useDeleteEstimateMutation();

  // Get the original handleSetEstimate function
  const originalHandleSetEstimate = useHandleSetEstimate();

  // Listen for changes in isViewingEstimateForm to detect when form closes
  useEffect(() => {
    if (!isViewingEstimateForm && refreshTrigger > 0) {
      // This means form was closed after submission
      setShowSuccessNotification(true);
      setSuccessMessage(
        isCreatingEstimate ? 'Estimate created successfully!' : 'Estimate updated successfully!'
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isViewingEstimateForm,
    refreshTrigger,
    isCreatingEstimate,
    selectedProject?.id,
    // fetchEstimates, <-- do not include this or we will get an infinite loop
  ]);

  const errorMessage = (error as Error)?.message;
  const [searchTerm, setSearchTerm] = useState('');

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    setSearchTerm(event.target.value);

  // Wrapper for the original handler that also triggers a refresh
  const handleSetValue = (action: HandleSetEstimateField, estimate: any) => {
    // Call the original handler
    originalHandleSetEstimate(action, estimate);

    // Increment refresh trigger to track form submissions
    if (action === 'create' || action === 'update') {
      setRefreshTrigger(prev => prev + 1);
    }
  };

  const filteredEstimates = estimateList?.filter(
    (estimate: GetProjectEstimateResponse.AsObject) =>
      (estimate.estimate?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ?? false) ||
      (estimate.estimate?.description?.toLowerCase().includes(searchTerm.toLowerCase()) ?? false) ||
      estimate.estimate?.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (estimate.estimate?.customid?.toString().toLowerCase().includes(searchTerm.toLowerCase()) ??
        false)
  );

  const handleCloseForm = () => {
    setIsViewingEstimateForm(false);
  };

  const handleCloseNotification = () => {
    setShowSuccessNotification(false);
  };

  const handleCloseErrorNotification = () => {
    setShowErrorNotification(false);
  };

  // Helper function to check if an estimate is associated with any contract
  const isEstimateAssociatedWithContract = (estimateId: string): boolean => {
    if (!contractList || !estimateId) return false;

    return contractList.some(contract =>
      contract.associatedestimatesList?.some(
        associatedEstimate => associatedEstimate.estimateid === estimateId
      )
    );
  };

  // Handle delete confirmation dialog
  const handleDeleteClick = (estimate: Estimate.AsObject) => {
    setEstimateToDelete(estimate);
    setDeleteDialogOpen(true);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setEstimateToDelete(null);
  };

  const handleDeleteConfirm = async () => {
    if (estimateToDelete) {
      try {
        await deleteEstimateMutation.mutateAsync(estimateToDelete);
        setDeleteDialogOpen(false);
        setEstimateToDelete(null);
        setShowSuccessNotification(true);
        setSuccessMessage('Estimate deleted successfully!');
      } catch (error) {
        console.error('Error deleting estimate:', error);
        setDeleteDialogOpen(false);
        setEstimateToDelete(null);

        // Show user-friendly error message
        setShowErrorNotification(true);
        setDeleteErrorMessage(
          'This estimate cannot be deleted. It may be referenced by other records in the system.'
        );
      }
    }
  };

  // Function to determine which table columns to show based on screen size
  const getVisibleColumns = () => {
    if (isMobile && !isLandscape) {
      // In portrait mode on mobile, show minimal columns
      return {
        estimateId: true,
        division: false,
        name: true,
        status: true,
        dateCreated: false,
        dueDate: false,
        actions: true,
      };
    } else if (isMobile && isLandscape) {
      // In landscape mode on mobile, show more columns
      return {
        estimateId: true,
        division: false,
        name: true,
        status: true,
        dateCreated: true,
        dueDate: false,
        actions: true,
      };
    } else if (isTablet) {
      // On tablets show most columns
      return {
        estimateId: true,
        division: true,
        name: true,
        status: true,
        dateCreated: true,
        dueDate: true,
        actions: true,
      };
    }
    // On desktop show all columns
    return {
      estimateId: true,
      division: true,
      name: true,
      status: true,
      dateCreated: true,
      dueDate: true,
      actions: true,
    };
  };

  const visibleColumns = getVisibleColumns();

  return (
    <Box sx={{ p: { xs: 1, sm: 2 } }} role="region" aria-label="Estimates list">
      <Box
        sx={{
          display: 'flex',
          gap: { xs: 1, sm: 2 },
          mb: { xs: 2, sm: 3 },
          alignItems: 'center',
          flexDirection: isMobile && !isLandscape ? 'column' : 'row',
          flexWrap: isMobile ? 'nowrap' : 'wrap',
        }}
      >
        <Button
          variant="contained"
          color="primary"
          onClick={event => {
            event.stopPropagation();
            handleSetValue('create', null);
          }}
          startIcon={<AddIcon aria-hidden="true" />}
          aria-label="Create new estimate"
          sx={{
            height: '40px',
            width: isMobile && !isLandscape ? '100%' : 'auto',
          }}
        >
          Create Estimate
        </Button>
        <TextField
          placeholder="Search Estimates..."
          value={searchTerm}
          onChange={handleSearchChange}
          size="small"
          variant="outlined"
          InputProps={{
            startAdornment: <SearchIcon aria-hidden="true" sx={{ mr: 1 }} />,
          }}
          id="estimate-search"
          aria-label="Search estimates"
          sx={{
            flexGrow: 1,
            minWidth: isMobile && !isLandscape ? '100%' : '250px',
            maxWidth: isMobile ? '100%' : '400px',
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderColor:
                  theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.23)'
                    : 'rgba(0, 0, 0, 0.23)',
              },
              '&:hover fieldset': {
                borderColor: theme.palette.primary.main,
              },
              '&.Mui-focused fieldset': {
                borderColor: theme.palette.primary.main,
              },
            },
          }}
        />
      </Box>

      {isLoading ? (
        <Box
          sx={{ display: 'flex', justifyContent: 'center', my: 4 }}
          role="status"
          aria-label="Loading estimates"
        >
          <CircularProgress aria-label="Loading" />
        </Box>
      ) : error ? (
        <Alert
          severity="error"
          sx={{
            mb: 2,
            '& .MuiAlert-icon': {
              color: theme.palette.error.main,
            },
          }}
          role="alert"
        >
          Error: {errorMessage}
        </Alert>
      ) : filteredEstimates && filteredEstimates.length > 0 ? (
        <TableContainer
          component={Paper}
          sx={{
            maxHeight: isMobile ? '60vh' : '70vh',
            backgroundColor: theme.palette.background.paper,
            boxShadow: theme.shadows[1],
            borderRadius: 1,
            overflow: 'auto',
          }}
        >
          <Table
            stickyHeader
            aria-label="Estimates table"
            role="grid"
            size={isMobile ? 'small' : 'medium'}
          >
            <TableHead>
              <TableRow>
                {visibleColumns.estimateId && (
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      backgroundColor:
                        theme.palette.mode === 'dark' ? theme.palette.background.paper : '#f5f5f5',
                      padding: isMobile ? 1 : undefined,
                      fontSize: isMobile ? '0.75rem' : undefined,
                    }}
                  >
                    Estimate ID
                  </TableCell>
                )}
                {visibleColumns.division && (
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      backgroundColor:
                        theme.palette.mode === 'dark' ? theme.palette.background.paper : '#f5f5f5',
                      padding: isMobile ? 1 : undefined,
                      fontSize: isMobile ? '0.75rem' : undefined,
                    }}
                  >
                    Division
                  </TableCell>
                )}
                {visibleColumns.name && (
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      backgroundColor:
                        theme.palette.mode === 'dark' ? theme.palette.background.paper : '#f5f5f5',
                      width: isMobile ? '35%' : '50%',
                      padding: isMobile ? 1 : undefined,
                      fontSize: isMobile ? '0.75rem' : undefined,
                    }}
                  >
                    Estimate Name
                  </TableCell>
                )}
                {visibleColumns.status && (
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      backgroundColor:
                        theme.palette.mode === 'dark' ? theme.palette.background.paper : '#f5f5f5',
                      width: isMobile ? '20%' : '15%',
                      padding: isMobile ? 1 : undefined,
                      fontSize: isMobile ? '0.75rem' : undefined,
                    }}
                  >
                    Status
                  </TableCell>
                )}
                {visibleColumns.dateCreated && (
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      backgroundColor:
                        theme.palette.mode === 'dark' ? theme.palette.background.paper : '#f5f5f5',
                      padding: isMobile ? 1 : undefined,
                      fontSize: isMobile ? '0.75rem' : undefined,
                    }}
                  >
                    Created
                  </TableCell>
                )}
                {visibleColumns.dueDate && (
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      backgroundColor:
                        theme.palette.mode === 'dark' ? theme.palette.background.paper : '#f5f5f5',
                      padding: isMobile ? 1 : undefined,
                      fontSize: isMobile ? '0.75rem' : undefined,
                    }}
                  >
                    Due Date
                  </TableCell>
                )}
                {visibleColumns.actions && (
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      backgroundColor:
                        theme.palette.mode === 'dark' ? theme.palette.background.paper : '#f5f5f5',
                      padding: isMobile ? 1 : undefined,
                      fontSize: isMobile ? '0.75rem' : undefined,
                    }}
                  >
                    Actions
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredEstimates.map(item => (
                <TableRow
                  key={item.estimate!.estimateid}
                  onClick={event => {
                    event.stopPropagation();
                    handleSetValue('setEstimate', item.estimate ?? null);
                  }}
                  hover
                  sx={{
                    cursor: 'pointer',
                    transition: 'background-color 0.2s',
                    '&:hover': {
                      backgroundColor: alpha(
                        theme.palette.primary.main,
                        theme.palette.mode === 'dark' ? 0.1 : 0.05
                      ),
                    },
                    '&:nth-of-type(even)': {
                      backgroundColor:
                        theme.palette.mode === 'dark'
                          ? alpha(theme.palette.common.white, 0.02)
                          : alpha(theme.palette.common.black, 0.02),
                    },
                  }}
                  role="row"
                  aria-label={`Estimate: ${item.estimate?.name || 'Unnamed estimate'}`}
                >
                  {visibleColumns.estimateId && (
                    <TableCell
                      sx={{
                        padding: isMobile ? 1 : undefined,
                        fontSize: isMobile ? '0.75rem' : undefined,
                      }}
                    >
                      {(item && item.estimate?.customid) ?? ''}
                    </TableCell>
                  )}
                  {visibleColumns.division && (
                    <TableCell
                      sx={{
                        padding: isMobile ? 1 : undefined,
                        fontSize: isMobile ? '0.75rem' : undefined,
                      }}
                    >
                      {(item && item.estimate?.masterformatdivision?.divisionname) ?? ''}
                    </TableCell>
                  )}
                  {visibleColumns.name && (
                    <TableCell
                      sx={{
                        padding: isMobile ? 1 : undefined,
                        fontSize: isMobile ? '0.75rem' : undefined,
                      }}
                    >
                      {(item && item.estimate?.name) ?? ''}
                    </TableCell>
                  )}
                  {visibleColumns.status && (
                    <TableCell
                      sx={{
                        padding: isMobile ? 1 : undefined,
                        fontSize: isMobile ? '0.75rem' : undefined,
                      }}
                    >
                      {item && item.estimate?.estimatestatusname ? (
                        <Tooltip
                          title={getStatusDescription(item.estimate.estimatestatusname)}
                          arrow
                          placement="top"
                        >
                          <Chip
                            label={item.estimate.estimatestatusname}
                            size={isMobile ? 'small' : 'medium'}
                            sx={{
                              ...getStatusChipProps(item.estimate.estimatestatusname, theme),
                              fontWeight: 500,
                              fontSize: isMobile ? '0.65rem' : '0.75rem',
                            }}
                          />
                        </Tooltip>
                      ) : (
                        ''
                      )}
                    </TableCell>
                  )}
                  {visibleColumns.dateCreated && (
                    <TableCell
                      sx={{
                        padding: isMobile ? 1 : undefined,
                        fontSize: isMobile ? '0.75rem' : undefined,
                      }}
                    >
                      {formatDateForDisplay(item.estimate?.datecreated)}
                    </TableCell>
                  )}
                  {visibleColumns.dueDate && (
                    <TableCell
                      sx={{
                        padding: isMobile ? 1 : undefined,
                        fontSize: isMobile ? '0.75rem' : undefined,
                      }}
                    >
                      {formatDateForDisplay(item.estimate?.duedate)}
                    </TableCell>
                  )}
                  {visibleColumns.actions && (
                    <TableCell
                      sx={{
                        padding: isMobile ? 1 : undefined,
                      }}
                    >
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip
                          title={`Edit estimate details for ${item.estimate?.name || 'this estimate'}`}
                          arrow
                          placement="top"
                        >
                          <IconButton
                            size="small"
                            onClick={event => {
                              event.stopPropagation();
                              handleSetValue('update', item.estimate ?? null);
                            }}
                            color="primary"
                            aria-label={`Edit estimate ${item.estimate?.name || 'unnamed estimate'}`}
                          >
                            <EditIcon fontSize="small" aria-hidden="true" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip
                          title={
                            isEstimateAssociatedWithContract(item.estimate?.estimateid || '')
                              ? `Cannot delete estimate associated with a contract`
                              : `Delete estimate ${item.estimate?.name || 'this estimate'}`
                          }
                          arrow
                          placement="top"
                        >
                          <span>
                            <IconButton
                              size="small"
                              onClick={event => {
                                event.stopPropagation();
                                handleDeleteClick(item?.estimate ?? ({} as Estimate.AsObject));
                              }}
                              color="error"
                              aria-label={`Delete estimate ${item.estimate?.name || 'unnamed estimate'}`}
                              disabled={isEstimateAssociatedWithContract(
                                item.estimate?.estimateid || ''
                              )}
                            >
                              <DeleteIcon fontSize="small" aria-hidden="true" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Box
          sx={{
            p: { xs: 2, sm: 4 },
            textAlign: 'center',
            backgroundColor: theme.palette.background.paper,
            borderRadius: 1,
            boxShadow: theme.shadows[1],
          }}
          role="status"
          aria-live="polite"
        >
          <Typography variant="body1" color="text.secondary">
            {searchTerm ? 'No estimates match your search criteria.' : 'No estimates available.'}
          </Typography>
        </Box>
      )}

      <Dialog
        open={isViewingEstimateForm}
        onClose={handleCloseForm}
        fullScreen={isMobile}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor:
              theme.palette.mode === 'dark' ? '#2c2c2c' : theme.palette.background.paper,
            color: theme.palette.text.primary,
            maxHeight: '75%',
          },
        }}
        aria-labelledby="estimate-dialog-title"
      >
        <DialogContent
          sx={{
            p: isMobile ? { xs: 1, sm: 2 } : 3,
          }}
        >
          <CreateEstimate />
        </DialogContent>
      </Dialog>

      {/* Success notification */}
      <Snackbar
        open={showSuccessNotification}
        autoHideDuration={4000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseNotification} severity="success" sx={{ width: '100%' }}>
          {successMessage}
        </Alert>
      </Snackbar>

      {/* Error notification */}
      <Snackbar
        open={showErrorNotification}
        autoHideDuration={6000}
        onClose={handleCloseErrorNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseErrorNotification} severity="error" sx={{ width: '100%' }}>
          {deleteErrorMessage}
        </Alert>
      </Snackbar>

      {/* Delete confirmation dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
        PaperProps={{
          sx: {
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.text.primary,
          },
        }}
      >
        <DialogTitle id="delete-dialog-title" sx={{ color: theme.palette.error.main }}>
          Delete Estimate
        </DialogTitle>
        <DialogContent>
          <Typography id="delete-dialog-description" variant="body1">
            Are you sure you want to delete the estimate "
            {estimateToDelete?.name || 'Unnamed Estimate'}"?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={handleDeleteCancel}
            color="primary"
            variant="outlined"
            disabled={deleteEstimateMutation.isLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleteEstimateMutation.isLoading}
            startIcon={
              deleteEstimateMutation.isLoading ? <CircularProgress size={16} /> : undefined
            }
          >
            {deleteEstimateMutation.isLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
